import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/vb";
import type { ProviderReceipt, ReceiptCitation } from "./verdictModel";

const EXCERPT_CHARS = 240;

/** Strip the markdown markers the models pepper into answers (**, ##, backticks,
 *  bullet dashes) so the inline teaser quote reads as clean prose. The full
 *  markdown is rendered in the dialog. */
function plainExcerpt(text: string): string {
  const t = (text || "")
    .replace(/[*_`#>]+/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "No answer was returned by this model.";
  return t.length > EXCERPT_CHARS ? t.slice(0, EXCERPT_CHARS).trimEnd() + "…" : t;
}

/** One cited-source row (shared by the receipt tile + the full-response dialog).
 *  Renders as a link that opens the source in a new tab. */
export function CiteRow({ cite }: { cite: ReceiptCitation }) {
  return (
    <a
      className="vb-cite"
      href={cite.url || undefined}
      target="_blank"
      rel="noopener noreferrer"
      title={cite.url}
    >
      <span className={cn("vb-cite__d", `vb-cite__d--${cite.orbit}`)} aria-hidden="true" />
      <span className="vb-cite__url">
        <span className="vb-cite__domain">{cite.domain}</span>
        {cite.path && <span className="vb-cite__path">{cite.path}</span>}
      </span>
      <span className="vb-cite__kind">{cite.kind}</span>
    </a>
  );
}

/** Per-model receipts (VB-F2): one column per provider with its verbatim
 *  excerpt, competitor mentions, and top cited sources. "See full response"
 *  opens the chat-style dialog with the complete answer + all sources. */
export function ReceiptColumns({
  providers,
  onOpenResponse,
}: {
  providers: ProviderReceipt[];
  onOpenResponse: (p: ProviderReceipt) => void;
}) {
  return (
    <div className="vb-receipt-cols">
      {providers.map((p) => {
        return (
          <div className="vb-receipt-col" key={p.providerKey}>
            <div className="vb-rc-hd">
              <Avatar model={p.providerKey} size="md" decorative />
              <span className="vb-rc-meta">
                <span className="vb-rc-name">{p.label}</span>
              </span>
            </div>

            <div className="vb-rc-quote">{plainExcerpt(p.answerText)}</div>

            {p.competitorMentions.length > 0 && (
              <div className="vb-rc-comps">
                <span className="vb-rc-comps__lbl">Competitors named</span>
                <div className="vb-rc-comps__pills">
                  {/* Per-answer mentions are presence flags, not frequencies,
                      so we show the named competitor without a count. */}
                  {p.competitorMentions.map((c) => (
                    <span className="vb-rc-comp" key={c.name}>
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="vb-rc-cited-hd">
              {p.citations.length > 0 ? `Cited ${p.citations.length} sources` : "No sources cited"}
            </div>
            {p.topCitations.length > 0 && (
              <div className="vb-rc-cites">
                {p.topCitations.map((c, i) => (
                  <CiteRow cite={c} key={`${c.url}-${i}`} />
                ))}
              </div>
            )}

            {p.citations.length > 0 && (
              <button type="button" className="vb-resp-trigger" onClick={() => onOpenResponse(p)}>
                See full response
                <ChevronRight size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ReceiptColumns;
